import { Modal, generateResolver, yup, Notification, STATUS_TYPES, ConfirmationPrompt, USER_PERMISSIONS } from 'dyl-components';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Icon, Popup, Modal as SemanticModal, Dropdown } from 'semantic-ui-react';
import groupsActions from 'actions/groups'
import campaignsActions from 'actions/campaigns';
import { BulkActionsContext } from "shared/context/BulkActionsProvider";
import { useSearchParams } from 'react-router-dom';
import RestrictedView from 'shared/RestrictedView';
const CAMPAIGN_LIMIT = 999;

const GroupBulkActionsModal = ({ open, onClose, numberSelectedGroups = 0, reload, setIsHeaderChecked, getSubgroups }) => {
    const [selectedGroups, setSelectedGroups] = useContext(BulkActionsContext);
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const [query] = useSearchParams();
    const search = query.get("search") || "";
    const { isDoingBulkAction } = useSelector((state) => state?.groups);

    const { control, reset, watch, formState: { isDirty, isValid }, handleSubmit } = useForm({
        mode: 'onChange',
        defaultValues: {
            type: ''
        },
        resolver: generateResolver({
            type: yup.string().required('This field is required')
        })
    });
    const { campaigns, isReadingCampaigns } = useSelector((state) => {
        return {
            campaigns: state.campaigns.campaigns,
            isReadingCampaigns: state.campaigns.isReadingCampaigns
        }
    });
    const typeWatch = watch('type');

    const dispatch = useDispatch();

    const close = () => {
        reset();
        onClose();
    }

    const bulkActionOptions = [
        {
            key: 'delete_groups', value: 'delete_groups', text: 'Delete Groups', userPermission: USER_PERMISSIONS.GROUPS_SUBGROUPS_DELETE, content: (
                <>
                    <Icon name='trash' />
                    Delete Groups
                </>
            )
        },
        {
            key: 'add_to_campaign', value: 'add_to_campaign', text: 'Add to Campaign', userPermission: USER_PERMISSIONS.CAMPAIGNS_EDIT, content: (
                <>
                    <Icon className={`fas fa-megaphone`} />
                    Add to Campaign
                </>
            )
        },
    ];

    const getSelectedIds = () => {
        const parentIds = selectedGroups.filter((group) => group.parent_label_id === undefined).map((group) => group.group_id);
        const includedIds = selectedGroups.filter((group) => !parentIds.includes(group.parent_label_id)).map((group) => group.group_id);
        return {
            parentIds,
            includedIds
        }
    }

    const onSave = async ({ type, campaign }) => {
        try {
            if (search) {
                const { parentIds, includedIds } = getSelectedIds();
                if (type === "add_to_campaign") {
                    const ids =
                        parentIds.length > 0
                            ? parentIds
                            : includedIds.length > 0
                                ? includedIds
                                : selectedGroups.map((group) => group.group_id);

                    await dispatch(
                        groupsActions.bulkAddToCampaign(
                            {
                                action: type,
                                campaign_id: campaign,
                                group_ids: ids,
                            },
                            { search: search }
                        )
                    );
                    Notification.alert(
                        'Successfully added to campaign!',
                        STATUS_TYPES.SUCCESS,
                        true
                    );
                    return;
                }

                const promises = []
                parentIds.forEach((group) => {
                    promises.push(getSubgroups({ parent_label_id: group }));
                });
                Promise.all(promises).then(async (results) => {
                    let data = []
                    results.forEach((result) => {
                        const ids = result?.data?.map((subgroup) => subgroup.id) || [];
                        data = data.concat(ids)
                    });
                    data = data.concat(includedIds);
                    data = [...new Set(data)];
                    await dispatch(groupsActions.deleteGroupsBulk(data));
                    Notification.alert('Successfully deleted groups!', STATUS_TYPES.SUCCESS, true);
                    close();
                    setSelectedGroups([]);
                    reload();
                    setIsHeaderChecked(false);
                    setIsPromptOpen(false);
                })
            } else {
                const selectedIds = selectedGroups.map((group) => group.group_id);
                if (type === "add_to_campaign") {
                    await dispatch(
                        groupsActions.bulkAddToCampaign({
                            action: type,
                            campaign_id: campaign,
                            group_ids: selectedIds,
                        })
                    );
                    Notification.alert(
                        'Successfully added to campaign!',
                        STATUS_TYPES.SUCCESS,
                        true
                    );
                    close();
                    setSelectedGroups([]);
                    reload();
                    setIsHeaderChecked(false);
                    setIsPromptOpen(false);
                    return;
                }
                await dispatch(groupsActions.deleteGroupsBulk(selectedIds));
                Notification.alert('Successfully deleted groups!', STATUS_TYPES.SUCCESS, true);
                close();
                setSelectedGroups([]);
                reload();
                setIsHeaderChecked(false);
                setIsPromptOpen(false);
            }
        } catch (error) {
            console.log(error)
            Notification.alert('Failed to delete groups!', STATUS_TYPES.ERROR, true);
        }
    }

    const isParentSelected = () => {
        const { parentIds } = getSelectedIds();
        return search && parentIds.length > 0;
    }

    useEffect(
        () =>
            dispatch(campaignsActions.readCampaigns({ limit: CAMPAIGN_LIMIT })),
        [dispatch]
    );

    return (
        <>
            <Modal open={open} onClose={close}>
                <Modal.Header>
                    Bulk Action <div style={{ float: 'right', marginRight: '1em' }}>
                        <small>{numberSelectedGroups} selected</small>
                        <Popup
                            trigger={<Icon size='small' style={{ marginLeft: '1em' }} floated='right' className='fas fa-circle-info' color='primary' />}
                            content={`Duplicates will be ignored`}
                            inverted
                            position='left center'
                            wide
                        />
                    </div>
                </Modal.Header>
                <Modal.Content>
                    <Form noValidate>
                        <Controller
                            name='type'
                            control={control}
                            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                <Form.Select
                                    {...value ? { text: bulkActionOptions.find(action => action.value === value)?.content } : {}}
                                    label='Bulk Action Type'
                                    placeholder='Select bulk action type'
                                    text={bulkActionOptions.find(item => item.value === value)?.content}
                                    value={value}
                                    options={bulkActionOptions}
                                    required
                                    error={error?.message}
                                    width={8}
                                    selectOnBlur={false}
                                >
                                    <Dropdown.Menu>
                                        {bulkActionOptions.map(item => (
                                            <RestrictedView type="disabled" userPermission={item.userPermission}>
                                                <Dropdown.Item key={item.key} value={item.value} text={item.content} active={value === item.value} selected={value === item.value} onClick={() => {
                                                    onChange(item.value);
                                                }} />
                                            </RestrictedView>
                                        ))}
                                    </Dropdown.Menu>
                                </Form.Select>
                            )}
                        />
                        {typeWatch === "add_to_campaign" && (
                            <Controller
                                name='campaign'
                                control={control}
                                render={({ field: { name, value, onChange } }) => (
                                    <Form.Select
                                        value={value}
                                        options={campaigns?.filter?.((campaign) => !campaign?.deleted)?.map?.((campaign) => ({ key: campaign?.id, text: campaign?.name, value: campaign?.id }))}
                                        onChange={(_, { value }) => {
                                            onChange({ target: { name, value } });
                                        }}
                                        placeholder={
                                            <div>
                                                <Icon
                                                    style={{ marginRight: 10 }}
                                                    name="search"
                                                />
                                                Select an Existing Campaign Name
                                            </div>
                                        }
                                        label='Campaign Name'
                                        loading={isReadingCampaigns}
                                        search
                                        width={15}
                                        selection
                                        required
                                        selectOnBlur={false}
                                    />
                                )}
                            />
                        )}
                    </Form>
                </Modal.Content>
                <Modal.Actions
                    hasSaveButton
                    saveDisabled={!isDirty || !isValid || isDoingBulkAction}
                    onSave={typeWatch === "add_to_campaign" ? handleSubmit(onSave) : () => setIsPromptOpen(true)}
                />
            </Modal>
            <SemanticModal open={isPromptOpen} style={{ padding: 30, width: 350 }}>
                <ConfirmationPrompt
                    header={<div style={{ paddingTop: '1em' }}>{isParentSelected() ? "Deleting the Group will also delete sub-groups." : "Are you sure?"}</div>}
                    subheader={isParentSelected() ? <div>Are you sure?</div> : <div>Selected groups will be permanently deleted.</div>}
                    onConfirm={handleSubmit(onSave)}
                    onCancel={() => setIsPromptOpen(false)}
                />
            </SemanticModal>
        </>
    )
}

export default GroupBulkActionsModal;
